
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

export interface ChartDataPoint {
  month: string;
  [key: string]: number | string;
}

interface HarvestData {
  expected_date: string | null;
  expected_quantity: number | null;
  farms: {
    product_name: string;
  } | null;
}

export interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  product: string;
  quantity: number;
  farmName: string;
  status: string;
}

export const useChartData = () => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch harvest data grouped by product and expected harvest month
        const { data, error } = await supabase
          .from('harvests')
          .select('id, expected_date, expected_quantity, status, farms!inner(id, product_name, farmer_name)')
          .not('expected_date', 'is', null)
          .not('expected_quantity', 'is', null);
          
        if (error) throw error;
        
        // Transform the data for the charts
        // Group by month and aggregate by product
        const monthlyData: Record<string, Record<string, number>> = {};
        const events: CalendarEvent[] = [];
        
        if (data) {
          data.forEach((harvest: any) => {
            if (!harvest.expected_date) return;
            
            const date = new Date(harvest.expected_date);
            const monthKey = format(date, 'MMM'); // Get month abbreviation (Jan, Feb, etc.)
            const productName = harvest.farms?.product_name || 'Unknown';
            const quantity = Number(harvest.expected_quantity) || 0;
            
            // Initialize month if it doesn't exist
            if (!monthlyData[monthKey]) {
              monthlyData[monthKey] = {};
            }
            
            // Add or accumulate the quantity for this product in this month
            if (!monthlyData[monthKey][productName]) {
              monthlyData[monthKey][productName] = 0;
            }
            
            monthlyData[monthKey][productName] += quantity;

            // Add to calendar events array
            events.push({
              id: harvest.id,
              title: `${productName} - ${quantity} kg`,
              date: date,
              product: productName,
              quantity,
              farmName: harvest.farms?.farmer_name || 'Unknown Farm',
              status: harvest.status
            });
          });
        }
        
        // Convert the processed data to array format for charts
        // Sort by month order
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const formattedData = months
          .filter(month => monthlyData[month]) // Only include months that have data
          .map(month => {
            const monthData: ChartDataPoint = { month };
            
            // Add each product's quantity to this month's data point
            Object.entries(monthlyData[month]).forEach(([product, quantity]) => {
              monthData[product] = quantity;
            });
            
            return monthData;
          });
        
        // If we don't have data, create sample data
        if (formattedData.length === 0) {
          // Use the predefined chart data as a fallback
          setChartData([
            { month: 'Jan', corn: 4000, wheat: 2400, potatoes: 2400 },
            { month: 'Feb', corn: 3000, wheat: 1398, potatoes: 2210 },
            { month: 'Mar', corn: 2000, wheat: 9800, potatoes: 2290 },
            { month: 'Apr', corn: 2780, wheat: 3908, potatoes: 2000 },
            { month: 'May', corn: 1890, wheat: 4800, potatoes: 2181 },
            { month: 'Jun', corn: 2390, wheat: 3800, potatoes: 2500 },
            { month: 'Jul', corn: 3490, wheat: 4300, potatoes: 2100 },
          ]);
        } else {
          setChartData(formattedData);
        }

        // Set calendar events
        setCalendarEvents(events);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setError(error instanceof Error ? error : new Error('Unknown error'));
        setIsLoading(false);
      }
    };

    fetchChartData();
  }, []);

  return { chartData, calendarEvents, isLoading, error };
};
